'use client';

import {
  ISalePageDetailResponse,
  ISalePageProductAdditionalInformResponse,
} from '@/api/types';
import { Button } from '@/components';
import { useIsLoggedIn } from '@/entities';
import { useLoginInducingModal } from '@/hooks/useLoginInducingModal';
import { qaAttr, qaId } from '@/lib/qa';
import { useOverlay } from '@vuddy/components';
import { trackEvent } from '@vuddy/utils';
import { useMemo } from 'react';
import { useCart } from '../../[salePageId]/contexts/CartContext';
import {
  DigitalPurchaseBottomSheet,
  PhysicalPurchaseBottomSheet,
} from '../bottomSheet';

interface InstantPurchaseCTAProps {
  salePage: Partial<ISalePageDetailResponse>;
  productAdditionalInforms?: ISalePageProductAdditionalInformResponse[];
  interactive?: boolean;
}

export const InstantPurchaseCTA = ({
  salePage,
  productAdditionalInforms,
  interactive,
}: InstantPurchaseCTAProps) => {
  const { openBottomSheet } = useOverlay();
  const hasPhysical = salePage?.products?.find(
    product => product.type === 'PHYSICAL' || product.type === 'FUNDING'
  );
  const {
    cartItems,
    setCartItems,
    selectedId,
    setSelectedId,
    selectedProductIds,
    setSelectedProductIds,
  } = useCart();

  const loginConfirm = useLoginInducingModal();
  const { isLoggedIn } = useIsLoggedIn();

  const labelText = useMemo(() => {
    if (salePage?.type === 'FUNDING') {
      return '참여하기';
    }

    if (salePage?.type === 'CLASS') {
      return '이용권 구매하기';
    }

    if (hasPhysical) {
      return '바로 구매';
    }

    return '바로 구매';
  }, [salePage?.type, hasPhysical]);

  const handleClick = () => {
    trackEvent('shop', 'click_sale_buy', {
      channel_id: salePage?.channelId,
      sale_type: salePage?.type,
    });

    if (!isLoggedIn) {
      loginConfirm();
      return;
    }

    if (hasPhysical) {
      openBottomSheet({
        component: PhysicalPurchaseBottomSheet,
        props: {
          salePageId: salePage?.id,
          interactive,
          cartItems: cartItems,
          setCartItems: setCartItems,
        },
      });
      return;
    }

    openBottomSheet({
      component: DigitalPurchaseBottomSheet,
      props: {
        salePageId: salePage?.id,
        interactive,
        initialSelectedId: selectedId,
        syncSelectedId: setSelectedId,
        initialSelectedIds: selectedProductIds,
        syncSelectedIds: setSelectedProductIds,
      },
    });
  };

  return (
    <Button
      {...qaAttr(qaId('sale.purchase', 'buy-now'))}
      size="large"
      fullWidth
      onClick={handleClick}>
      {labelText}
    </Button>
  );
};
